import { Link, useNavigate } from 'react-router-dom'
import { useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Phone, Mail, MapPin } from 'lucide-react'
import { SITE } from '../constants/site'
import { getVersion } from '../api/version'

export function Footer() {
  const { data: version } = useQuery({ queryKey: ['version'], queryFn: getVersion })
  const navigate = useNavigate()
  const clicks = useRef(0)
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Accès admin discret : 5 clics rapprochés sur le numéro de version → /login.
  const handleVersionClick = () => {
    if (resetTimer.current) clearTimeout(resetTimer.current)
    clicks.current += 1
    if (clicks.current >= 5) {
      clicks.current = 0
      navigate('/login')
      return
    }
    resetTimer.current = setTimeout(() => { clicks.current = 0 }, 1500)
  }

  return (
    <footer className="bg-navy text-white/85">
      <div className="mx-auto grid max-w-6xl gap-10 px-5 py-14 lg:grid-cols-3 lg:px-8">
        <div>
          <div className="flex items-center gap-2.5">
            <img src="/img/logo-maison-gilles.png" alt="" className="h-12 w-12" />
            <img src="/img/logo-complet.png" alt="Maison Gilles SA" className="h-6 w-auto" />
          </div>
          <p className="mt-4 max-w-xs text-sm leading-relaxed text-white/70">
            {SITE.baseline}. Une histoire de famille depuis {SITE.depuis}, transmise sur quatre
            générations.
          </p>
          <div className="mt-5 flex gap-3">
            <a href={SITE.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" className="transition-opacity hover:opacity-80">
              <img src="/img/logo-facebook.png" alt="Facebook" className="h-9 w-9" />
            </a>
            <a href={SITE.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" className="transition-opacity hover:opacity-80">
              <img src="/img/logo-instagram.png" alt="Instagram" className="h-9 w-9" />
            </a>
          </div>
        </div>

        <div>
          <h3 className="font-display text-lg font-semibold text-white">Nous trouver</h3>
          <ul className="mt-4 space-y-3 text-sm text-white/75">
            <li className="flex gap-3">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-orange" />
              <span>{SITE.adresse}<br />{SITE.cp} {SITE.ville}</span>
            </li>
            <li className="flex gap-3">
              <Phone className="mt-0.5 h-4 w-4 shrink-0 text-orange" />
              <a href={`tel:${SITE.telephoneLien}`} className="hover:text-white">{SITE.telephone}</a>
            </li>
            <li className="flex gap-3">
              <Mail className="mt-0.5 h-4 w-4 shrink-0 text-orange" />
              <a href={`mailto:${SITE.email}`} className="hover:text-white">{SITE.email}</a>
            </li>
          </ul>
        </div>

        <div>
          <h3 className="font-display text-lg font-semibold text-white">Horaires</h3>
          <ul className="mt-4 space-y-1.5 text-sm text-white/75">
            <li className="flex justify-between gap-4"><span>Lundi</span><span className="text-orange">Fermé</span></li>
            <li className="flex justify-between gap-4"><span>Mardi au Samedi</span><span className="text-white">9h00 – 18h00</span></li>
            <li className="flex justify-between gap-4"><span>Dimanche</span><span className="text-orange">Fermé</span></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-5 py-5 text-xs text-white/50 sm:flex-row lg:px-8">
          <span>
            © {SITE.depuis}–{new Date().getFullYear()} Maison Gilles · TVA {SITE.tva} · Tous droits réservés.
            {version && (
              <span
                onClick={handleVersionClick}
                className="cursor-default select-none text-white/30"
                aria-hidden="true"
              > · v{version}</span>
            )}
          </span>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            <Link to="/mentions-legales" className="transition-colors hover:text-white/80">Mentions légales</Link>
            <Link to="/confidentialite" className="transition-colors hover:text-white/80">Politique de confidentialité</Link>
          </div>
        </div>
      </div>
    </footer>
  )
}
